import React from 'react';
import { Link } from 'react-router-dom';
import { ButtonSkeleton, DataTitle, DataValue, NoData, PageCard, Poppins, Spacer, TitleWithBack } from 'src/common';
import ExpandCollapse from 'src/common/ExpandCollapse';
import Button from 'src/components/form/Button';
import { FrameworkControlQuery, useAddControlsFromLibraryMutation, useControlsRmpCheckQuery } from 'src/graphql/_generated';
import { useCache } from 'src/graphql/useCache';
import { useAuth } from 'src/state/auth';
import styled from 'styled-components';

const Card = styled(PageCard)`
  .head {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .grid-view {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-gap: 50px;
    max-width: 1100px;

    &--1-3 {
      grid-template-columns: 1fr 3fr;
      padding-right: 100px;
    }
  }
  .panel {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    grid-gap: 15px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .grid-view {
      grid-gap: 10px;
      grid-template-columns: 1fr 1fr;
      &--1-3 {
        grid-template-columns: 1fr 1fr;
        padding-right: 0;
      }
    }
  }
`;

interface MetaSheetProps {
  data: NonNullable<FrameworkControlQuery['frameworkControl']>;
}

export const MetaSheet: React.FC<MetaSheetProps> = ({ data }) => {
  const { refetchQuery } = useCache();
  const { workspaceId } = useAuth();
  const { data: controls, loading: controlsLoading } = useControlsRmpCheckQuery();

  const [addControlsFromLibrary, { loading }] = useAddControlsFromLibraryMutation({
    onCompleted: () => refetchQuery({ include: ['controlsRmpCheck'] }),
  });

  const isIn = controls?.controls.some((el) => el.frameworkControls.some((fc) => fc.id === data.id));

  return (
    <Card data-cy="framework-control-page">
      <div className="head">
        <TitleWithBack title="CONTROL" gradient />
        {controlsLoading ? (
          <ButtonSkeleton />
        ) : isIn ? (
          <Poppins px={14} color="cflowerBlue">
            Control added to Risk Management Plan
          </Poppins>
        ) : (
          <Button
            primary
            disabled={loading}
            onClick={() =>
              addControlsFromLibrary({
                variables: { controlIds: [{ id: data.id }], workspaceId },
              })
            }
            data-cy="add-to-rmp"
          >
            + ADD CONTROL TO RMP
          </Button>
        )}
      </div>
      <Spacer $px={30} />
      <Poppins px={32} weight={600} data-cy="control-title">
        {data.name}
      </Poppins>
      <Spacer $px={44} />
      <div className="grid-view">
        <div>
          <DataTitle>STRATEGY</DataTitle>
          {data.strategy ? <DataValue>{data.strategy}</DataValue> : <NoData />}
        </div>
      </div>
      <Spacer $px={30} />
      <div>
        <DataTitle>FRAMEWORK</DataTitle>
        {data.controlFramework?.name ? (
          <DataValue>
            <Link to={`/control-frameworks/${data.controlFramework.id}`}>{data.controlFramework.name}</Link>
          </DataValue>
        ) : (
          <NoData />
        )}
      </div>
      <Spacer $px={25} />
      <div>
        <DataTitle>DESCRIPTION</DataTitle>
        {data.description ? <ExpandCollapse text={data.description} /> : <NoData />}
      </div>
    </Card>
  );
};
